import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import i18next from "i18next";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import App from "./App";
import { ApiError } from "./api/client";
import { initLanguage } from "./i18n";
import { recordVisit } from "./lib/visit";
import { applyTheme, initialTheme } from "./stores/themeStore";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Данные квестов меняются редко: перезапрос при каждом фокусе окна
      // только дёргает сеть и мигает интерфейсом.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Повторять запрос, на который сервер ответил «нет прав» или
        // «не найдено», бессмысленно — ответ не изменится.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

// Часть текстов приходит с сервера — названия и описания квестов переводятся
// там же. Кэш react-query о языке ничего не знает, и после переключения
// каталог остался бы на прежнем языке до истечения staleTime. Инвалидация
// висит на событии i18next, а не на переключателе: к моменту события
// resolvedLanguage уже новый, и перезапрос уходит с нужным Accept-Language.
i18next.on("languageChanged", () => {
  void queryClient.invalidateQueries();
});

// Тема проставляется до первого кадра: иначе выбравший светлую увидит
// вспышку тёмного фона, пока приложение поднимается.
applyTheme(initialTheme());

recordVisit();

// Язык готовится до первого рендера: иначе выбравший русский увидит, как
// страница мигает английским и перерисовывается.
void initLanguage().then(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
});
